import * as React from 'react';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import { Theme, createStyles, makeStyles } from '@material-ui/core';
import { SkeletonLoader } from '../../components/loading/SkeletonLoader';
import { AnimatedItem } from '../../components/animations/AnimatedItem';
import { MatterPartyData } from '../parties/PartiesInfo';
import { RvStaffCard } from './StaffCard';
import { ApplicationsLabelSettingsConfig, MATTER_DETAILS_REFRESH_EVENT } from '../MatterDetails';
import EventSystem from '../../EventSystem';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        grid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
            gap: theme.spacing(2.5),
            alignItems: 'start',
        },
    })
);

interface ExternalProps {
    id: string;
    labelSettingsConfig: ApplicationsLabelSettingsConfig;
}

interface Data {
    loading?: boolean;
    networkStatus?: number;
    matter: {
        relationships: MatterPartyData;
    };
    refetch: () => void;
}

interface MatterStaffInfoProps extends ExternalProps {
    data: Data;
}

const RvMatterStaffInfo: React.FC<MatterStaffInfoProps> = (props) => {
    const classes = useStyles();
    const refetch = props.data.refetch;

    React.useEffect(() => {
        EventSystem.on(MATTER_DETAILS_REFRESH_EVENT, refetch);
        return () => { EventSystem.event.removeListener(MATTER_DETAILS_REFRESH_EVENT, refetch); };
    // tslint:disable-next-line: align
    }, [refetch]);

    if (props.data.loading) {
        return <SkeletonLoader variant="panel" columns={2} rows={3} wrapInPaper={false} />;
    }

    var staffCards = new Array<{}>();

    if (props.data.matter && props.data.matter.relationships) {
        var relationships = props.data.matter.relationships.relationships;
        var idx = 0;
        relationships.map((relationship) => {
            relationship.individuals.map((staff) => {
                idx++;
                staffCards.push(
                    <AnimatedItem key={idx} noFill delay={(idx - 1) * 30}>
                        <RvStaffCard
                            data={staff}
                            phoneNumbers={staff.phoneNumbers}
                            emailAddresses={staff.emailAddresses}
                            addresses={staff.addresses}
                            relatedParties={staff.relatedParties}
                            relatedIndividuals={staff.relatedIndividuals}
                            relatedOrganisations={staff.relatedOrganisations}
                            labelSettingsConfig={props.labelSettingsConfig}
                        />
                    </AnimatedItem>
                );
            });
        });
    }

    return <div className={classes.grid}>{staffCards}</div>;
};

const MatterStaffInfoData = gql`
query StaffInfoData($id: Int!){
    matter(id: $id) {
        id,
        relationships(includeResponsibleStaff: true) {
            recordCount
            relationships {
                individuals {
                    id  
                    partyId 
                    displayName 
                    matterRole
                    matterRoleId
                    phoneNumbers {
                        type
                        number
                        extension
                        sequence
                    }
                    emailAddresses {
                        type
                        email
                        sequence
                    }
                    addresses {
                        type
                        display
                        sequence
                    }
                }
            }
        }
    }
}`;

// tslint:disable-next-line:no-any
export const MatterStaffInfo = graphql<any, ExternalProps, any>
    (MatterStaffInfoData, {
        // Was inheriting the app's global default (cache-and-network) - always re-fetching on
        // every tab mount regardless of cache. Cached after the first load; the header's
        // Refresh button (MatterDetails.tsx) forces a re-fetch via MATTER_DETAILS_REFRESH_EVENT.
        options: ({ id }) => ({ variables: { id: id }, fetchPolicy: 'cache-first' })
    })
    (RvMatterStaffInfo);